import { Controller } from '@hotwired/stimulus';
import { Dropdown } from 'bootstrap';
import { fireChangeEvent } from '../search/accordion_change';
import { createOptionRenderer } from '../../utils/select/option_renderer';
import { searchOptions } from '../../utils/select/search_handler';
import { getAccordionLabel, getDisplayText } from '../../utils/select/display_handler';
import {
  addOptionToSelection,
  checkGroup,
  checkGroupItems,
  removeSelectedOptionById,
} from '../../utils/select/selection_handler';

export default class SelectController extends Controller {
  static values = {
    placeholder: String,
    choices: { type: Array, default: [] },
    value: { type: Array, default: [] },
    multiple: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    searchAtStart: { type: Boolean, default: false },
    maxSelectedChoices: { type: Number, default: null },
  };

  options = [];
  flatOptions = [];
  selectedOptions = [];
  _initialized = false;
  isLoading = false;

  static targets = [
    'input',
    'option',
    'placeholder',
    'optionsContainer',
    'toggleButton',
    'optionTemplate',
    'multiselectOptionTemplate',
    'optionGroupTemplate',
    'selectedOptionsTemplate',
    'loadingStateTemplate',
  ];

  connect() {
    this._initialized = true;
    this.valueValue = this.valueValue.map((val) => Number(val));
    this.optionRenderer = createOptionRenderer({
      templates: {
        option: this.optionTemplateTarget,
        multiselectOption: this.multiselectOptionTemplateTarget,
        optionGroup: this.optionGroupTemplateTarget,
        selectedOptions: this.selectedOptionsTemplateTarget,
        loading: this.loadingStateTemplateTarget,
      },
      multiple: this.multipleValue,
      maxSelectedChoices: this.maxSelectedChoicesValue,
      fieldId: this.element.dataset.id,
    });

    this._updateOptions(this.choicesValue || []);
    fireChangeEvent(this.element, getAccordionLabel(this.selectedOptions));

    this.dropdown = Dropdown.getOrCreateInstance(this.element);
  }

  onUpdateChoices(e) {
    if (e.detail?.choices === null) {
      this._showLoadingState();
      return;
    }

    const choices = e.detail?.choices || [];

    this.selectedOptions = [];
    this.valueValue = [];
    this.flatOptions = [];
    this._refreshInputLabel();
    this._updateOptions(choices);
    this.isLoading = false;
  }

  onDropdownShow() {
    if (!this.isLoading && this.searchableValue) {
      this._refreshOptions(this.options);
    }

    if (!this.multipleValue) {
      this._scrollToSelectedOption();
    }
  }

  onDropdownHide() {
    this._refreshInputLabel();
  }

  onDeleteAllSelectionsClick(e) {
    e.stopPropagation();
    this.selectedOptions = [];
    this.valueValue = [];
    this._refreshInputLabel();
    this._refreshOptions(this.options);
  }

  onInputClick(e) {
    if (this.searchableValue) {
      e.target.value = '';
    }
  }

  onSearchInput(e) {
    const query = e.target.value.toLowerCase();

    if (query === '') {
      this._refreshOptions(this.options);
      return;
    }

    const filteredOptions = searchOptions(this.options, query, this.searchAtStartValue);
    this._refreshOptions(filteredOptions, true);
    this.dropdown.show();
  }

  // non-multiple - option click
  onRadioOptionChange(e) {
    this.optionTargets.forEach((option) => {
      option.parentElement.classList.remove('option--selected');
    });

    const value = Number(e.target.value);
    const isSelected = this.selectedOptions[0]?.id === value;
    if (isSelected) {
      this.selectedOptions = [];
      this.valueValue = [];
      this._refreshOptions(this.options);
      return;
    }

    const selectedOption = this.options.find((option) => option.id === value);
    this.selectedOptions = [selectedOption];
    this.valueValue = [value];

    e.target.parentElement.classList.add('option--selected');

    this.dropdown.hide();
  }

  onOptionLabelClick(e) {
    e.stopPropagation();
  }

  // multiple - option click
  onOptionChange(e) {
    const value = Number(e.target.value);
    const checked = e.target.checked;
    const selectedOption = this.flatOptions.find((option) => option.id === value);

    this.selectedOptions = checked
      ? addOptionToSelection(this.selectedOptions, selectedOption)
      : removeSelectedOptionById(this.selectedOptions, value);

    if (selectedOption.children) {
      // check all children
      this.selectedOptions = checkGroupItems(
        selectedOption.children,
        checked,
        this.selectedOptions,
        this._checkElement
      );
    }

    // check parent (group)
    if (selectedOption.group) {
      this.selectedOptions = checkGroup(
        selectedOption.group,
        this.flatOptions,
        this.selectedOptions,
        this._checkElement
      );
    }

    this.valueValue = this.selectedOptions.map((option) => option.id);

    if (this.maxSelectedChoicesValue) {
      this.optionTargets.forEach((option) => {
        option.disabled = !option.checked && this.selectedOptions.length >= this.maxSelectedChoicesValue;
      });
    }
  }

  choicesValueChanged() {
    if (!this._initialized) {
      return;
    }

    this.selectedOptions = [];
    this.valueValue = [];
    this.flatOptions = [];
    this._refreshInputLabel();
    this._updateOptions(this.choicesValue);
  }

  valueValueChanged() {
    if (!this._initialized) {
      return;
    }

    this._refreshInputLabel();

    this.inputTarget.dispatchEvent(
      new CustomEvent('select:input-change', {
        detail: { value: this.selectedOptions },
        bubbles: true,
      })
    );

    fireChangeEvent(this.element, getAccordionLabel(this.selectedOptions));
  }

  // OPTIONS MANAGEMENT
  _updateOptions(newOptions) {
    this.options = newOptions;
    this._initOptions(newOptions);
    this._refreshInputLabel();
    this._refreshOptions(newOptions);
  }

  _initOptions(options, group) {
    options.forEach((option) => {
      this.flatOptions = [...this.flatOptions, { ...option, group }];
      if (this.valueValue.includes(option.id)) {
        this.selectedOptions = [...this.selectedOptions, option];
      }

      if (option.children) {
        this._initOptions(option.children, option.id);
      }
    });
  }

  _refreshOptions(options, isSearching = false) {
    this._clearOptions();
    this.optionRenderer.addOptions(options, this.optionsContainerTarget, this.selectedOptions, isSearching);
  }

  _clearOptions() {
    this.optionsContainerTarget.innerHTML = '';
  }

  _refreshInputLabel() {
    this.inputTarget.value = getDisplayText(this.selectedOptions);
  }

  _checkElement = (id, isChecked) => {
    const el = this.optionTargets.find((input) => Number(input.value) === id);
    if (el) {
      el.checked = isChecked;
    }
  };

  _scrollToSelectedOption() {
    if (!this.selectedOptions.length) {
      return;
    }

    requestAnimationFrame(() => {
      const selectedOptionElement = this.optionTargets.find(
        (input) => Number(input.value) === this.selectedOptions[0].id
      );

      if (selectedOptionElement) {
        this.optionsContainerTarget.scrollTo({
          top: selectedOptionElement.parentElement.offsetTop,
          behavior: 'instant',
        });
      }
    });
  }

  _showLoadingState() {
    this._clearOptions();
    this.isLoading = true;
    this.optionRenderer.showLoading(this.optionsContainerTarget);
  }
}
